import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer",
    {
        variants: {
            variant: {
                default: "bg-primary text-primary-foreground shadow-sm hover:brightness-110 hover:shadow-md active:brightness-95",
                destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 active:bg-destructive/80",
                outline: "border border-border bg-card hover:bg-secondary hover:text-secondary-foreground active:bg-secondary/80",
                secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/70 active:bg-secondary/60",
                ghost: "hover:bg-secondary hover:text-secondary-foreground active:bg-secondary/80",
                link: "text-primary underline-offset-4 hover:underline font-medium",
                success: "bg-success text-success-foreground shadow-sm hover:bg-success/90 active:bg-success/80",
            },
            size: {
                default: "h-9 px-4 py-2",
                sm: "h-8 rounded-lg px-3 text-xs",
                lg: "h-10 rounded-lg px-6 text-sm",
                icon: "h-9 w-9",
            },
        },
        defaultVariants: { variant: "default", size: "default" },
    },
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
        <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref as any} {...props} />
    );
});
Button.displayName = "Button";

export { Button, buttonVariants };
